import { baseApi } from '@/api/baseApi';
import { parseWithSchema } from '@/lib/safeParse';
import type {
    CancelSubscriptionRequest,
    CancelSubscriptionResponse,
    ExtendSubscriptionRequest,
    ExtendSubscriptionResponse,
    GetSubscriptionResponse,
    ListSubscriptionsFilters,
    ListSubscriptionsResponse
} from '@/lib/subscriptionsSchemas';
import {
    CancelSubscriptionResponseSchema,
    ExtendSubscriptionResponseSchema,
    GetSubscriptionResponseSchema,
    ListSubscriptionsResponseSchema
} from '@/lib/subscriptionsSchemas';

/**
 * RTK Query endpoints для admin-backend /subscriptions/*.
 * admin-backend на порту 5002 без префикса /admin.
 */
export const subscriptionsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listSubscriptions: build.query<ListSubscriptionsResponse, ListSubscriptionsFilters | void>({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.status) {
                    search.status = params.status;
                }
                if (params?.search !== undefined && params.search !== '') {
                    search.search = params.search;
                }
                if (params?.userId) {
                    search.userId = params.userId;
                }
                if (params?.limit !== undefined) {
                    search.limit = String(params.limit);
                }
                if (params?.offset !== undefined) {
                    search.offset = String(params.offset);
                }
                return {
                    url: '/subscriptions',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): ListSubscriptionsResponse =>
                parseWithSchema(ListSubscriptionsResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((s) => ({
                            type: 'Subscription' as const,
                            id: s.id
                        })),
                        { type: 'Subscription' as const, id: 'LIST' }
                    ]
                    : [{ type: 'Subscription' as const, id: 'LIST' }]
        }),

        getSubscription: build.query<GetSubscriptionResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/subscriptions/${id}`,
                method: 'GET'
            }),
            transformResponse: (raw: unknown): GetSubscriptionResponse =>
                parseWithSchema(GetSubscriptionResponseSchema, raw),
            providesTags: (_result, _error, arg) => [
                { type: 'Subscription' as const, id: arg.id }
            ]
        }),

        cancelSubscription: build.mutation<
            CancelSubscriptionResponse,
            { id: string; body: CancelSubscriptionRequest }
        >({
            query: ({ id, body }) => ({
                url: `/subscriptions/${id}/cancel`,
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): CancelSubscriptionResponse =>
                parseWithSchema(CancelSubscriptionResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Subscription', id: arg.id },
                { type: 'Subscription', id: 'LIST' }
            ]
        }),

        extendSubscription: build.mutation<
            ExtendSubscriptionResponse,
            { id: string; body: ExtendSubscriptionRequest }
        >({
            query: ({ id, body }) => ({
                url: `/subscriptions/${id}/extend`,
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): ExtendSubscriptionResponse =>
                parseWithSchema(ExtendSubscriptionResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Subscription', id: arg.id },
                { type: 'Subscription', id: 'LIST' }
            ]
        })
    })
});

export const {
    useListSubscriptionsQuery,
    useGetSubscriptionQuery,
    useCancelSubscriptionMutation,
    useExtendSubscriptionMutation
} = subscriptionsApi;
